import { useEffect, useState } from 'react'
import type { components } from '../../lib/api/schema.gen'
import { getSessionState, subscribeSessionState } from '../../lib/auth/session-store'
import type { SessionState } from '../../lib/auth/session-store'
import {
  type RecentlyViewedEntry,
  readRecentlyViewed,
  recordRecentlyViewed,
  subscribeRecentlyViewed,
} from './recently-viewed-store'
import type { ConfigurableResolveState } from './useConfigurableResolve'

type Sourceable = components['schemas']['Sourceable']
type CatalogItemAvailability = components['schemas']['CatalogItemAvailability']

/** The shared capture debounce window (ADR/spec-mandated ~1200ms) —
 * `ConfiguratorCore`'s `useConfigurableResolve` re-fires on every `sku`
 * change (scrubbing a dimension pill emits many resolves); only the resolved
 * SKU that stays settled for this long after its OWN resolve gets recorded,
 * so a scrub yields one history entry, not ten.
 *
 * EXPORTED (UID-460 code-review W1) because `SeriesConfiguratorDrawer`'s own
 * reactive "Case A" capture — a resolved row keyed on the row's identity
 * rather than on a resolve state — needs the SAME window for the SAME
 * reason: toggling axis pills walks through several resolved rows, and
 * history dedupes by SKU (so DISTINCT rows are NOT deduped against each
 * other) with only `MAX_ENTRIES` slots. Shared as one constant so the two
 * capture paths can never drift apart. */
export const RECENTLY_VIEWED_RECORD_DEBOUNCE_MS = 1200

function currentUserId(state: SessionState = getSessionState()): string | null {
  return state.status === 'authenticated' ? state.session.userId : null
}

// ── V1 availability-gap synthesis (user's explicit call, see architect's
// spec) ─────────────────────────────────────────────────────────────────────
// A configurator resolve only ever hands back a bare `Sourceable` — no
// `availability`/`category` from the wire (unlike V2's `CatalogResultCard`,
// which already carries both real). Rather than omit those fields and leave
// a half-empty tile/modal, synthesize a plausible, DETERMINISTIC placeholder
// — never a fabricated quantity (`availableQuantity` stays `null`, mirroring
// `apex-automation.ts`'s own `recentlyViewedAvailability` heuristic and its
// `stableDemoHash` — duplicated here, not imported, for the same "tiny, pure,
// no-external-behavior, nothing to drift" reason that file's own copy gives).

function stableHash(input: string): number {
  let hash = 0
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) >>> 0
  }
  return hash
}

/** Reuses categories already present in the apex-automation demo dataset, so
 * a synthesized V1 entry reads as a plausible part of the SAME catalog
 * rather than an obviously-fake label. */
const PLACEHOLDER_CATEGORIES: readonly string[] = [
  'Shafts & Couplings',
  'Bearings',
  'Fasteners',
  'Linear Motion',
]

const PLACEHOLDER_LEAD_TIME_TIERS: ReadonlyArray<{ readonly min: number; readonly max: number }> = [
  { min: 1, max: 2 },
  { min: 2, max: 3 },
  { min: 3, max: 5 },
  { min: 5, max: 8 },
]

export function synthesizePlaceholderCategory(seed: string): string {
  return PLACEHOLDER_CATEGORIES[stableHash(seed) % PLACEHOLDER_CATEGORIES.length] ?? 'Catalog'
}

/** The longest lead-time tier reads as "made to order" (mirroring
 * `apex-automation.ts`'s inStock:false ↔ longer-lead pairing); every shorter
 * tier reads as "in stock" — the demo heuristic never emits `'unknown'`,
 * same posture as the existing fixture. */
export function synthesizePlaceholderAvailability(seed: string): CatalogItemAvailability {
  const hash = stableHash(seed)
  const tierIndex =
    Math.floor(hash / PLACEHOLDER_CATEGORIES.length) % PLACEHOLDER_LEAD_TIME_TIERS.length
  const tier = PLACEHOLDER_LEAD_TIME_TIERS[tierIndex] ?? PLACEHOLDER_LEAD_TIME_TIERS[0]
  const isLongLead = tierIndex === PLACEHOLDER_LEAD_TIME_TIERS.length - 1
  const status: CatalogItemAvailability['status'] = isLongLead ? 'made_to_order' : 'in_stock'
  const statusLabel = isLongLead ? 'Made to order' : 'In stock'
  return {
    availableQuantity: null,
    status,
    note: `${statusLabel} · ${tier?.min}–${tier?.max} days`,
    leadTimeDays: tier ?? { min: 0, max: 0 },
  }
}

/**
 * Read hook — `RecentlyViewed.tsx`'s (and `CatalogBrowsePage`'s, to decide
 * whether to render the section at all) data source. Session-scoped via the
 * current `SessionInfo.userId`; re-reads on every session-identity change
 * AND on every write anywhere else in the tab (`subscribeRecentlyViewed`,
 * needed because capture can happen from a sibling subtree on the SAME page
 * — e.g. `ConfiguratorDrawer` opened from a `ResultCard` on `/catalog`
 * alongside this row).
 */
export function useRecentlyViewed(): readonly RecentlyViewedEntry[] {
  const [userId, setUserId] = useState<string | null>(() => currentUserId())
  const [entries, setEntries] = useState<readonly RecentlyViewedEntry[]>(() =>
    userId ? readRecentlyViewed(userId) : [],
  )

  useEffect(
    () =>
      subscribeSessionState((state) => {
        setUserId(currentUserId(state))
      }),
    [],
  )

  useEffect(() => {
    function refresh() {
      setEntries(userId ? readRecentlyViewed(userId) : [])
    }
    refresh()
    return subscribeRecentlyViewed(refresh)
  }, [userId])

  return entries
}

/**
 * V1 capture — wired into `ConfiguratorCore` right after `useConfigurableResolve`.
 * Debounces on the resolve state itself: a NEW resolve (any status change,
 * including another `'resolved'` sourceable from a scrub) cancels the
 * pending timer and starts a fresh one, so only the value that stays
 * `'resolved'` for {@link RECENTLY_VIEWED_RECORD_DEBOUNCE_MS} without a further
 * change gets recorded — exactly once. `idle`/`loading`/`not-available`/
 * `error` never record. A resolved sourceable with no `catalogSku` is not
 * recordable (the dedup key) and is skipped; likewise a resolve with no
 * signed-in user (nothing to key the history to).
 */
export function useRecordConfiguratorView(resolveState: ConfigurableResolveState): void {
  useEffect(() => {
    if (resolveState.status !== 'resolved') return
    const sourceable: Sourceable = resolveState.sourceable
    const catalogSku = sourceable.catalogSku
    if (!catalogSku) return

    const timer = window.setTimeout(() => {
      const userId = currentUserId()
      if (!userId) return
      recordRecentlyViewed(userId, {
        catalogSku,
        sourceable,
        availability: synthesizePlaceholderAvailability(sourceable.id),
        category: synthesizePlaceholderCategory(sourceable.id),
      })
    }, RECENTLY_VIEWED_RECORD_DEBOUNCE_MS)

    return () => window.clearTimeout(timer)
  }, [resolveState])
}

/**
 * V2 capture — an imperative call (not a hook) for
 * `CatalogSeriesConfigurePage`'s row-expand handler, which already has a
 * fully-resolved `CatalogResultCard` in hand (real `availability`/`category`
 * off the wire, no synthesis needed) at the moment it decides to expand a
 * row with `!hasOpenDimensions(card)`. Skipped the same way as V1 when there
 * is no `catalogSku` (the dedup key) or no signed-in user.
 */
export function recordCatalogRowView(card: {
  readonly sourceable: Sourceable
  readonly availability: CatalogItemAvailability
  readonly category?: string
}): void {
  const catalogSku = card.sourceable.catalogSku
  if (!catalogSku) return
  const userId = currentUserId()
  if (!userId) return
  recordRecentlyViewed(userId, {
    catalogSku,
    sourceable: card.sourceable,
    availability: card.availability,
    ...(card.category !== undefined ? { category: card.category } : {}),
  })
}
